import type { TFunction } from 'i18next';
import type { i18n as I18nInstance } from 'i18next';
import { parseOpenInvoiceDate } from './openBalanceDebtScope.js';

interface InvoiceRow {
  document_category?: string;
  document_type: number;
  document_type_name: string;
  document_number: number;
  document_date: string;
  amount: number;
  balance?: number | null;
  open_amount?: number | null;
  balance_status?: 'provider' | 'local_overlay' | 'closure_metadata' | 'unknown';
  document_link?: string | null;
}

interface PrintOpenInvoicesOptions {
  customerName: string;
  customerId: number;
  phone?: string | null;
  documents: InvoiceRow[];
  fromDate: string;
  toDate: string;
  t: TFunction;
  i18n: I18nInstance;
  printWindow?: Window | null;
  balanceIncomplete?: boolean;
  balanceDiscrepancy?: number | null;
}

function formatNIS(amount: number): string {
  return amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatOpenInvoiceDate(value: string): string {
  if (!value) return '—';
  const d = parseOpenInvoiceDate(value);
  if (!d) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(2);
  return `${dd}-${mm}-${yy}`;
}

function openAmount(doc: InvoiceRow): number | null {
  if (doc.balance_status === 'unknown') return null;
  const value = doc.open_amount ?? doc.balance;
  return value == null || !Number.isFinite(Number(value)) ? null : Math.abs(Number(value));
}

function getStatus(doc: InvoiceRow): string {
  if (doc.document_category === 'receipt') return 'receipt';
  const bal = openAmount(doc);
  if (bal == null) return 'unknown';
  if (bal <= 0) return 'closed';
  if (bal < doc.amount) return 'partial';
  return 'open';
}

function getStatusLabel(status: string, t: TFunction): string {
  switch (status) {
    case 'open': return t('invoiceReport.statusOpen', 'פתוחה');
    case 'closed': return t('invoiceReport.statusClosed', 'סגורה');
    case 'partial': return t('invoiceReport.statusPartial', 'מזוכת חלקית');
    case 'receipt': return t('invoiceReport.statusReceipt', 'קבלה');
    case 'unknown': return t('documents.openAmountUnavailable', 'Unavailable');
    default: return status;
  }
}

function escapeHtml(value: string | number | null | undefined): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function safeDocumentHref(value?: string | null): string | null {
  if (!value) return null;
  try {
    const baseUrl = typeof window !== 'undefined' ? window.location?.origin : undefined;
    const url = baseUrl ? new URL(value, baseUrl) : new URL(value);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    return escapeHtml(url.href);
  } catch {
    return null;
  }
}

export function printOpenInvoicesReport({
  customerName,
  customerId,
  phone,
  documents,
  fromDate,
  toDate,
  t,
  i18n,
  printWindow: providedPrintWindow,
  balanceIncomplete = false,
  balanceDiscrepancy,
}: PrintOpenInvoicesOptions): boolean {
  // Filter to invoices only (exclude receipts, delivery notes, etc.) that have an open balance
  const openInvoices = documents
    .filter((doc) => {
      if (doc.document_category !== 'invoice') return false;
      const bal = openAmount(doc);
      return bal != null && bal > 0;
    })
    .sort((left, right) => {
      const leftDate = parseOpenInvoiceDate(left.document_date);
      const rightDate = parseOpenInvoiceDate(right.document_date);
      if (!leftDate && !rightDate) return 0;
      if (!leftDate) return 1;
      if (!rightDate) return -1;
      return leftDate.getTime() - rightDate.getTime();
    });

  const totalAmount = openInvoices.reduce((sum, d) => sum + d.amount, 0);
  const totalRemaining = openInvoices.reduce((sum, d) => sum + (openAmount(d) || 0), 0);
  const incomplete = balanceIncomplete || documents.some(doc => doc.document_category === 'invoice' && openAmount(doc) == null);
  const balanceNotes = [
    incomplete ? t('collectionWorkflow.incomplete', 'Balances are incomplete; the amount shown is a known subtotal, not verified total debt.') : '',
    balanceDiscrepancy != null && Math.abs(balanceDiscrepancy) > 0.01
      ? `${t('collectionWorkflow.discrepancy', 'Account / open-document discrepancy')}: ${formatNIS(balanceDiscrepancy)}`
      : '',
  ].filter(Boolean);

  const dir = i18n.dir();
  const printWindow = providedPrintWindow ?? window.open('', '_blank');
  if (!printWindow) return false;

  const rows = openInvoices.map((doc, idx) => {
    const status = getStatus(doc);
    const statusLabel = getStatusLabel(status, t);
    const remaining = openAmount(doc) || 0;
    const href = safeDocumentHref(doc.document_link);
    const linkStart = href ? `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color:#2563eb;text-decoration:none;">` : '';
    const linkEnd = href ? '</a>' : '';

    return `<tr>
      <td style="text-align:center;">${idx + 1}</td>
      <td>${escapeHtml(customerName)}</td>
      <td style="text-align:center;">${linkStart}${doc.document_number}${linkEnd}</td>
      <td class="date-cell">${formatOpenInvoiceDate(doc.document_date)}</td>
      <td style="text-align:end;">${formatNIS(doc.amount)}</td>
      <td style="text-align:center;">${escapeHtml(statusLabel)}</td>
      <td style="text-align:end;font-weight:600;">${formatNIS(remaining)}</td>
    </tr>`;
  }).join('\n') || `<tr><td colspan="7" style="text-align:center;color:#777;padding:14px;">${escapeHtml(t('invoiceReport.noOpenInvoices', 'No open invoices'))}</td></tr>`;

  const now = new Date();
  const dateStr = `${now.getDate()}.${now.getMonth() + 1}.${now.getFullYear()}`;
  const timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

  const title = t('invoiceReport.title', 'Customer Debt Report');
  const rowsLabel = t('invoiceReport.rows', 'שורות');
  const totalLabel = t('invoiceReport.total', 'סה"כ');
  const remainingLabel = t('invoiceReport.remaining', 'נותר לתשלום');

  printWindow.document.write(`<!DOCTYPE html>
<html lang="${escapeHtml(i18n.language)}">
<head>
  <meta charset="UTF-8" />
  <title>${escapeHtml(title)} - ${escapeHtml(customerName)}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    html {
      direction: ltr;
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
      font-size: 12px;
      line-height: 1.5;
      color: #1a1a1a;
      padding: 10mm 12mm;
      direction: ltr;
      width: 100%;
      max-width: 100%;
    }
    .report-shell {
      direction: ${escapeHtml(dir)};
      width: 100%;
      max-width: 100%;
    }

    .header {
      text-align: center;
      margin-bottom: 16px;
      padding-bottom: 10px;
      border-bottom: 2px solid #2563eb;
    }
    .header h1 { font-size: 20px; color: #2563eb; margin-bottom: 2px; }
    .header .subtitle { font-size: 12px; color: #555; }

    .report-title {
      font-size: 16px;
      font-weight: 700;
      margin: 12px 0 8px;
    }

    .summary-bar {
      display: flex;
      justify-content: flex-end;
      gap: 24px;
      margin-bottom: 12px;
      font-size: 12px;
    }
    .summary-bar .item { display: flex; gap: 6px; }
    .summary-bar .label { color: #555; }
    .summary-bar .value { font-weight: 700; }

    .date-range {
      display: flex;
      justify-content: flex-end;
      gap: 16px;
      margin-bottom: 8px;
      font-size: 11px;
      color: #555;
    }

    table {
      width: 100%;
      max-width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
      margin-bottom: 12px;
    }
    th {
      background: #f0f4ff;
      font-weight: 600;
      font-size: 11px;
      padding: 6px 8px;
      border: 1px solid #d0d5dd;
      overflow-wrap: anywhere;
    }
    td {
      padding: 5px 8px;
      border: 1px solid #e0e0e0;
      font-size: 11px;
      overflow-wrap: anywhere;
    }
    tbody tr:nth-child(even) { background: #fafbfd; }
    .date-cell {
      direction: ltr;
      text-align: center;
      white-space: nowrap;
    }

    .footer-summary {
      display: flex;
      justify-content: flex-end;
      gap: 24px;
      margin-top: 8px;
      font-size: 12px;
    }
    .footer-summary .item { display: flex; gap: 6px; }
    .footer-summary .label { color: #555; }
    .footer-summary .value { font-weight: 700; }

    .print-meta {
      margin-top: 24px;
      font-size: 9px;
      color: #999;
      text-align: ${dir === 'rtl' ? 'left' : 'right'};
    }

    a { color: #2563eb; text-decoration: none; }
    a:hover { text-decoration: underline; }

    @media print {
      body { padding: 0; }
      @page { size: A4 landscape; margin: 8mm; }
    }
  </style>
</head>
<body>
<main class="report-shell" dir="${escapeHtml(dir)}">
  <div class="header">
    <h1>${escapeHtml(customerName)}</h1>
    <div class="subtitle">#${customerId}${phone ? ` · ${escapeHtml(phone)}` : ''}</div>
  </div>

  <div class="report-title">${escapeHtml(title)}</div>

  <div class="date-range">
    <span>${escapeHtml(t('invoiceReport.fromDate', 'מתאריך'))}: <bdi>${formatOpenInvoiceDate(fromDate)}</bdi></span>
    <span>${escapeHtml(t('invoiceReport.toDate', 'עד תאריך'))}: <bdi>${formatOpenInvoiceDate(toDate)}</bdi></span>
  </div>

  <div class="summary-bar">
    <div class="item"><span class="label">${escapeHtml(rowsLabel)}:</span><span class="value">${openInvoices.length}</span></div>
    <div class="item"><span class="label">${escapeHtml(totalLabel)}:</span><span class="value">${formatNIS(totalAmount)}</span></div>
    <div class="item"><span class="label">${escapeHtml(remainingLabel)}:</span><span class="value">${formatNIS(totalRemaining)}</span></div>
  </div>

  <table>
    <colgroup>
      <col style="width:5%">
      <col style="width:30%">
      <col style="width:13%">
      <col style="width:14%">
      <col style="width:13%">
      <col style="width:11%">
      <col style="width:14%">
    </colgroup>
    <thead>
      <tr>
        <th style="text-align:center;">#</th>
        <th>${escapeHtml(t('invoiceReport.colName', 'שם'))}</th>
        <th style="text-align:center;">${escapeHtml(t('invoiceReport.colDocNumber', "מס' מסמך"))}</th>
        <th style="text-align:center;">${escapeHtml(t('invoiceReport.colDate', 'הופק בתאריך'))}</th>
        <th style="text-align:end;">${escapeHtml(t('invoiceReport.colTotal', 'סה"כ'))}</th>
        <th style="text-align:center;">${escapeHtml(t('invoiceReport.colStatus', 'סטטוס'))}</th>
        <th style="text-align:end;">${escapeHtml(t('invoiceReport.colRemaining', 'נותר לתשלום'))}</th>
      </tr>
    </thead>
    <tbody>
      ${rows}
    </tbody>
  </table>

  <div class="footer-summary">
    <div class="item"><span class="label">${escapeHtml(rowsLabel)}:</span><span class="value">${openInvoices.length}</span></div>
    <div class="item"><span class="label">${escapeHtml(totalLabel)}:</span><span class="value">${formatNIS(totalAmount)}</span></div>
    <div class="item"><span class="label">${escapeHtml(remainingLabel)}:</span><span class="value">${formatNIS(totalRemaining)}</span></div>
  </div>

  ${balanceNotes.map(note => `<div class="print-meta">${escapeHtml(note)}</div>`).join('')}
  <div class="print-meta">${dateStr} ${timeStr}</div>
</main>
</body>
</html>`);
  printWindow.document.close();

  setTimeout(() => {
    printWindow.focus();
    printWindow.print();
  }, 400);
  return true;
}
